<template>
  <div
    class="lesson-card"
    @click="$emit('card-clicked')"
    role="button"
    tabindex="0"
    @keydown.enter="$emit('card-clicked')"
    @keydown.space.prevent="$emit('card-clicked')"
  >
    <div class="card-content">
      <div
        class="card-image"
        :style="`background-image: url('${
          lesson.thumbnail || '/images/placeholder-lesson.jpg'
        }')`"
      >
        <div class="card-overlay"></div>
      </div>

      <div class="like-icon-container" v-if="canLike">
        <img
          v-if="isLiked && isLogged"
          height="40"
          src="/images/liked-01.svg"
          alt="Remove from favourites"
          class="like-icon"
          @click.stop="handleLikeClick"
          @keydown.enter.stop="handleLikeClick"
          @keydown.space.prevent.stop="handleLikeClick"
          role="button"
          tabindex="0"
        />
        <img
          v-else-if="!isLiked && isLogged"
          height="40"
          src="/images/like-01.svg"
          alt="Add to favourites"
          class="like-icon"
          @click.stop="handleLikeClick"
          @keydown.enter.stop="handleLikeClick"
          @keydown.space.prevent.stop="handleLikeClick"
          role="button"
          tabindex="0"
        />
        <img
          v-else
          height="30"
          src="/images/like-brown.svg"
          alt=""
          class="like-icon"
          style="cursor: not-allowed"
        />
      </div>

      <div class="item-info-container">
        <div class="item-info">
          <div class="title-section">
            {{ lesson.title }}
          </div>
        </div>

        <div class="line-breaker"></div>

        <p class="card-description">
          {{ lesson.short_description }}
        </p>

        <div class="grade-info" v-if="formattedGrades">
          <span class="grade-label">Grade:</span>
          <span class="grade-value">{{ formattedGrades }}</span>
        </div>

        <div class="subject-duration-row">
          <div class="subject-info" v-if="formattedSubjects">
            <span class="subject-label">Subject:</span>
            <span class="subject-value">{{ formattedSubjects }}</span>
          </div>
          <div class="duration-info">
            <span class="duration-label">Duration:</span>
            <span class="duration-value"
              >{{ lesson.duration || 0 }} Minutes</span
            >
          </div>
        </div>

        <div class="tags-section" v-if="allTags.length">
          <div v-for="(tag, index) in allTags" :key="index" class="item-tag">
            {{ tag }}
          </div>
          <div v-if="remainingTagsCount > 0" class="more-tags">
            {{ remainingTagsCount }}+ more
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */

import { defineProps, ref, computed, defineEmits, watch } from "vue";
import LessonLibraryService from "@/services/lessonLibraryService";

const props = defineProps({
  lesson: {
    type: Object,
    required: true,
  },
  isLogged: {
    type: Boolean,
    default: true,
  },
  canLike: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["card-clicked", "update-liked-status"]);

const isLiked = ref(props.lesson.isLiked || false);

watch(
  () => props.lesson.isLiked,
  (newValue) => {
    isLiked.value = newValue || false;
  }
);

const formattedGrades = computed(() => {
  return props.lesson.grade?.map((g) => g.title).join(", ") || "";
});

const formattedSubjects = computed(() => {
  return props.lesson.subject?.map((s) => s.title).join(", ") || "";
});

const MAX_TAGS = 3;

const allTags = computed(() => {
  const domainTags =
    props.lesson.domain?.slice(0, MAX_TAGS).map((d) => d.title) || [];
  const remaining = MAX_TAGS - domainTags.length;
  const softwareTags =
    remaining > 0
      ? props.lesson.software?.slice(0, remaining).map((s) => s.title) || []
      : [];
  return [...domainTags, ...softwareTags];
});

const remainingTagsCount = computed(() => {
  const totalDomains = props.lesson.domain?.length || 0;
  const totalSoftware = props.lesson.software?.length || 0;
  const total = totalDomains + totalSoftware;
  return total > MAX_TAGS ? total - MAX_TAGS : 0;
});

const handleLikeClick = async () => {
  const previousLikedState = isLiked.value;
  isLiked.value = !isLiked.value;

  try {
    const response = await LessonLibraryService.likeLesson(props.lesson.id);
    if (response.status !== 200) {
      isLiked.value = previousLikedState;
    } else {
      emit("update-liked-status", {
        id: props.lesson.id,
        liked: isLiked.value,
      });
    }
  } catch (err) {
    isLiked.value = previousLikedState;
    console.error("Error liking lesson:", err.message);
  }
};
</script>

<style scoped>
.lesson-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  height: 450px;
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.lesson-card:hover {
  transform: translateY(-5px);
}

.card-content {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.card-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  z-index: 1;
}

.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.6);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
}

.lesson-card:hover .card-overlay {
  opacity: 1;
}

.like-icon-container {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
}

.like-icon {
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}

.like-icon:hover {
  transform: scale(1.1);
  filter: brightness(0.9);
}

.item-info-container {
  position: absolute;
  text-align: left;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1rem;
  padding-bottom: 40px;
  height: 280px;
  z-index: 3;
  transform: translateY(calc(100% - 110px));
  transition:
    transform 0.4s ease,
    padding-bottom 0.4s ease;
  background-color: #f2f3fb;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-radius: 0 0 12px 12px;
}

.item-info-container::after {
  content: "";
  position: absolute;
  top: -25px;
  left: -25px;
  right: -25px;
  height: 20%;
  background-color: #f2f3fb;
  opacity: 0;
  filter: blur(8px);
  z-index: 5;
  transition: opacity 0.3s ease;
}

.lesson-card:hover .item-info-container {
  transform: translateY(0px);
  padding-bottom: 1rem;
}

.lesson-card:hover .item-info-container::after {
  opacity: 1;
}

.line-breaker {
  width: 100%;
  height: 1px;
  border-bottom: 2px dashed #98a2b3;
  margin: 4px 0;
  opacity: 0;
}

.lesson-card:hover .line-breaker {
  opacity: 1;
}

.item-info {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0;
  z-index: 6;
}

.title-section {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xxl);
  font-weight: 700;
  line-height: 1.4;
  color: var(--primary-blue);
  text-align: left;
  word-break: break-word;
  flex: 1;
  margin-right: 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-description {
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  line-height: 1.4;
  color: #40444b;
  margin-top: -0.25rem;
  margin-bottom: -0.25rem;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.1s;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lesson-card:hover .card-description {
  opacity: 1;
  transform: translateY(0);
}

.grade-info {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  line-height: 1.25;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.2s;
}

.lesson-card:hover .grade-info {
  opacity: 1;
  transform: translateY(0);
}

.grade-label {
  font-weight: 600;
  color: #40444b;
}

.grade-value {
  font-weight: 400;
  color: #3b4648;
}

.subject-duration-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  line-height: 1.25;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.3s;
}

.lesson-card:hover .subject-duration-row {
  opacity: 1;
  transform: translateY(0);
}

.subject-info,
.duration-info {
  display: flex;
  gap: 0.25rem;
}

.subject-label,
.duration-label {
  font-weight: 600;
  color: #40444b;
}

.subject-value,
.duration-value {
  font-weight: 400;
  color: #3b4648;
}

.tags-section {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.4s;
}

.lesson-card:hover .tags-section {
  opacity: 1;
  transform: translateY(0);
}

.item-tag {
  background-color: #ffffff;
  border-radius: 4px;
  padding: 0.25rem 0.75rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #40444b;
  border: 1px solid var(--neutral-100);
  white-space: nowrap;
}

.more-tags {
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #3b4648;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .lesson-card {
    height: 250px;
  }

  .item-info-container {
    z-index: 20;
    transform: translateY(calc(100% - 50px)) !important;
    padding-bottom: 40px !important;
    pointer-events: auto;
  }

  .title-section {
    -webkit-line-clamp: 1;
    line-clamp: 1;
    font-size: var(--fs-small);
  }

  .subject-duration-row {
    flex-direction: column;
    gap: 0px;
  }

  .like-icon-container {
    top: 5px;
    right: 5px;
    z-index: 21;
  }

  /* Disable all hover transitions on mobile */
  .lesson-card:hover {
    transform: none;
  }

  .lesson-card:hover .card-overlay {
    opacity: 0;
  }

  .lesson-card:hover .line-breaker {
    opacity: 0;
  }

  .lesson-card:hover .grade-info {
    opacity: 0;
    transform: translateY(20px);
  }

  .lesson-card:hover .subject-duration-row {
    opacity: 0;
    transform: translateY(20px);
  }

  .lesson-card:hover .tags-section,
  .lesson-card:hover .card-description {
    opacity: 0;
    transform: translateY(20px);
  }

  .lesson-card:hover .item-info-container::after {
    opacity: 0;
  }
}
</style>
